/**
 * 订单状态类型
 * - 0: 等待买家付款
 * - 1: 付款成功
 * - 2: 支付关闭
 * - 30: 已发货
 * - 31: 已收货
 * - 40: 已换货
 * - 41: 换货已发货
 * - 50: 退款关闭/交易关闭
 * - 60: 交易成功
 * - -1: 订单异常
 * - -10: 抵用异常
 * - -11: 积分抵用异常
 * - 99: 已删除
 */
export enum OrderStatus {
  /** 等待买家付款 */
  WAITING_FOR_PAYMENT = 0,

  /** 付款成功 */
  PAYMENT_SUCCESS = 1,

  /** 支付关闭 */
  PAYMENT_CLOSED = 2,

  /** 已发货 */
  SHIPPED = 30,

  /** 已收货 */
  DELIVERED = 31,

  /** 已换货 */
  EXCHANGED = 40,

  /** 换货已发货 */
  EXCHANGE_SHIPPED = 41,

  /** 交易关闭 */
  REFUND_CLOSED = 50,

  /** 交易成功 */
  TRANSACTION_SUCCESS = 60,

  /** 订单异常 */
  ORDER_EXCEPTION = -1,

  /** 抵用异常 */
  REDEMPTION_EXCEPTION = -10,

  /** 积分抵用异常 */
  INTEGRAL_REDEMPTION_EXCEPTION = -11,

  /** 超时后付款异常 */
  TIMEOUT_EXCEPTION = -2,

  /** 已删除 */
  DELETED = 99,
}

/**
 * 退款状态类型
 * - 0：无
 * - 1：申请中
 * - 2: 退款处理中
 * - 11：退款成功
 * - 21：换货中
 * - 25：换货成功
 * - 50：退款关闭
 */
export enum RefundStatus {
  /** 无 */
  NONE = 0,

  /** 申请中 */
  APPLYING = 1,

  /** 退款处理中 */
  REFUND_APPLYING = 2,

  /** 退款成功 */
  REFUND_SUCCESS = 11,

  /** 换货中 */
  EXCHANGING = 21,

  /** 换货成功 */
  EXCHANGE_SUCCESS = 25,

  /** 退款关闭 */
  REFUND_CLOSED = 50,
}

/**
 * 优惠券状态枚举
 * - Expired: -1, 过期
 * - Normal: 0, 正常
 * - Using: 1, 正在使用中
 * - ToBeClaimed: 5, 将领取
 * - Used: 10, 已使用
 * - Gift: 100, 赠送券包特殊赋予状态
 */
export enum VoucherStatus {
  /** 过期 */
  Expired = -1,

  /** 正常 */
  Normal = 0,

  /** 正在使用中 */
  Using = 1,

  /** 待领取 */
  ToBeClaimed = 5,

  /** 已使用 */
  Used = 10,

  /** 赠送券包特殊赋予状态 */
  Gift = 100,
}

/**
 * 商品类型
 * - 0: 实物商品（PHYSICAL）
 * - 1: 虚拟商品（VIRTUAL）
 * - 2: 虚拟+实物商品（MIXED）
 * - 3: 采购商品（PROCUREMENT）
 */
export enum GoodsType {
  /** 实物商品 */
  PHYSICAL = 0,

  /** 虚拟商品*/
  VIRTUAL = 1,

  /** 虚拟+实物商品 */
  MIXED = 2,

  /** @description: 采购商品*/
  PROCUREMENT = 3,
}

/**
 * 页面状态枚举
 */
export enum PageState {
  /** 加载中 */
  LOADING = "loading",

  /** 无数据 */
  NO_DATA = "nodata",

  /** 页面出错 */
  ERROR = "error",

  /** 网络错误 */
  NET_ERROR = "netError",

  /** 优惠券过期 */
  COUPON_EXPIRED = "couponExpired",

  /** 需要登录（登录已失效） */
  LOGIN_REQUIRED = "loginRequired",

  /** 请求被服务端限流 */
  REQUEST_LIMIT = "requestLimit",

  /** 加载成功 */
  SUCCESS = "over",
}

/**
 * 促销活动类型
 * - -1: 全部（ALL）
 * - 0: 满折（DISCOUNT）
 * - 1: 满减（REDUCTION）
 * - 2: 满赠（GIFT）
 * - 3: 换购（EXCHANGE）
 * - 4: 一口价（BUNDLE_PRICE）
 * - 5: 奖励金加倍抵（INTEGRAL_RATE_DEDUCT）
 */
export enum PromotionType {
  /** 全部 */
  ALL = -1,

  /**
   * 满折
   * - 门槛：金额、件数
   * - 活动说明：购买指定商品现价达x元/x件，可享n折
   */
  DISCOUNT = 0,

  /**
   * 满减
   *  - 门槛：金额、件数
   * - 活动说明：购买指定商品现价达x元/x件，可减n元
   */
  REDUCTION = 1,

  /**
   * 满赠
   *  - 门槛：金额、件数
   * - 活动说明：购买指定商品实付价达x元或买满x件，可0元获得赠品*/
  GIFT = 2,

  /**
   * 换购
   *  - 门槛：金额
   * - 活动说明：购买指定商品满x元，可优惠购买指定商品
   */
  EXCHANGE = 3,

  /**
   * 一口价
   *  - 门槛：件数
   * - 活动说明：购买指定商品满x件，可享受组合价
   */
  BUNDLE_PRICE = 4,

  /**
   * 奖励金加倍抵
   *  - 门槛：金额
   * - 活动说明：购买制定商品满x元，可享奖励金抵扣n％ */
  INTEGRAL_RATE_DEDUCT = 5,
}

/**
 * 促销门槛类型
 * - 1: 达到金额（AMOUNT）
 * - 2: 达到件数（QUANTITY）
 */
export enum ThresholdType {
  /** 达到金额 */
  AMOUNT = 1,

  /** 达到件数 */
  QUANTITY = 2,
}

/**
 * 优惠类型
 * - 0: 折扣（DISCOUNT）
 * - 1: 优惠金额（REDUCTION）
 */
export enum RewardType {
  /** 折扣 */
  DISCOUNT = 0,

  /** 优惠金额 */
  REDUCTION = 1,
}

/**
 * 分享配置类型
 * - 1: 首页分享配置（INDEX_SHARE）
 * - 2: 专题分享配置（THEME_SHARE）
 * - 3: 商品详情分享配置（DETAIL_SHARE）
 * - 4: 商品详情分享配置（ACTIVITY_SHARE）
 */
export enum shareConfigType {
  /** 首页分享配置 */
  INDEX_SHARE = 1,

  /** 专题分享配置 */
  THEME_SHARE = 2,

  /** 商品详情分享配置 */
  DETAIL_SHARE = 3,

  /** 活动分享配置 */
  ACTIVITY_SHARE = 4,

  /** h5分享配置 */
  H5_SHARE = 5,
}

/**
 * 活动用户奖品领取状态
 * - Pending: 0, 待领取
 * - Claimed: 1, 已领取
 * - Refunded: 2, 已退款
 * - RefundFailed: 3, 退款失败
 * - Used: 4, 已使用
 * - ClaimFailed: -1, 领取失败
 */
export enum ActivityUserAwardState {
  /** 待领取 */
  Pending = 0,

  /** 已领取 */
  Claimed = 1,

  /** 已退款 */
  Refunded = 2,

  /** 退款失败 */
  RefundFailed = 3,

  /** 已使用 */
  Used = 4,

  /** 领取失败 */
  ClaimFailed = -1,
}

/** 奖品类型枚举 */
export enum AwardTypeEnum {
  NDayVIP = "NDayVIP",
  ReceiveVoucher = "ReceiveVoucher",
  Integral = "ReceiveIntegral",
  EmptyNoRecord = "EmptyNoRecord",
}

/**
 * 售后状态类型
 * - 1: 申请中
 * - 2: 商家审核超时
 * - 3: 审核通过
 * - 4: 审核不通过
 * - 5: 用户寄件
 * - 6: 商家收货
 * - 7: 商家拒绝收货
 * - 10: 支付中心申请退款中
 * - 11: 退款成功
 * - 20: 换货成功
 * - 100: 用户取消申请
 * - 101: 商家审核不通过超时取消申请
 * - 102: 用户寄件超时取消申请
 * - 103: 商家拒绝收货用户超时取消申请
 */
export enum AfterSalesStatus {
  /** 申请中 */
  APPLYING = 1,

  /** 商家审核超时 */
  MERCHANT_REVIEW_TIMEOUT = 2,

  /** 审核通过 */
  APPROVED = 3,

  /** 审核不通过 */
  REJECTED = 4,

  /** 用户寄件 */
  USER_SHIPPING = 5,

  /** 商家收货 */
  MERCHANT_RECEIVED = 6,

  /** 商家拒绝收货 */
  MERCHANT_REJECT_RECEIVED = 7,

  /** 支付中心申请退款中 */
  REFUND_PROCESSING = 10,

  /** 退款成功 */
  REFUND_SUCCESS = 11,

  /** 换货成功 */
  EXCHANGE_SUCCESS = 20,

  /** 用户取消申请 */
  USER_CANCELLED = 100,

  /** 商家审核不通过超时取消申请 */
  REVIEW_TIMEOUT_CANCELLED = 101,

  /** 用户寄件超时取消申请 */
  SHIPPING_TIMEOUT_CANCELLED = 102,

  /** 商家拒绝收货用户超时取消申请 */
  REJECT_RECEIVED_TIMEOUT_CANCELLED = 103,
}

/**
 * 申请退款类型
 * - 1: 退款（REFUND）
 * - 2: 退货退款（RETURN_AND_REFUND）
 */
export enum ApplyRefundType {
  /** 退货退款 */
  REFUND_ONLY = 1,

  /** 换货 */
  RETURN_AND_REFUND = 2,
}

/**
 * 退款原因类型
 * - 1: 多拍/拍错/不想要
 * - 2: 买贵了/少用优惠
 * - 3: 商品与描述不符
 * - 4: 商品破损/有瑕疵
 * - 5: 快递丢件/物流异常
 * - 6: 未按约定时间发货
 * - 7: 商家发错货
 * - 8: 缺货
 * - 999: 其他
 */
export enum RefundReasonType {
  /** 多拍/拍错/不想要 */
  WRONG_ORDER = 1,

  /** 买贵了/少用优惠 */
  OVERPRICED = 2,

  /** 商品与描述不符 */
  DESCRIPTION_MISMATCH = 3,

  /** 商品破损/有瑕疵 */
  DAMAGED = 4,

  /** 快递丢件/物流异常 */
  LOGISTICS_ISSUE = 5,

  /** 未按约定时间发货 */
  LATE_SHIPMENT = 6,

  /** 商家发错货 */
  WRONG_ITEM = 7,

  /** 缺货 */
  OUT_OF_STOCK = 8,

  /** 其他 */
  OTHER = 999,
}

/**
 * 促销商品类型
 * - 1: 赠品（GIFT）
 * - 2: 换购（EXCHANGE）
 */
export enum PromotionGoodsType {
  /** 无类型 */
  NONE = 0,

  /** 赠品 */
  GIFT = 1,

  /** 换购 */
  EXCHANGE = 2,
}

/**
 * 拒绝理由类型
 * - 1: 退回商品不完好
 * - 10: 退货商品与订单商品不一致
 * - 20: 空包裹
 * - 30: 已协商完毕不退款
 * - 40: 已超过15天无理由退款时限
 * - 50: 已发货,请选择退货退款
 * - 60: 已协商完毕不退款
 * - 999: 其他
 */
export enum RejectReasonType {
  /** 退回商品不完好 */
  ITEM_DAMAGED = 1,

  /** 退货商品与订单商品不一致 */
  ITEM_MISMATCH = 10,

  /** 空包裹 */
  EMPTY_PACKAGE = 20,

  /** 已协商完毕不退款 */
  NEGOTIATION_COMPLETED_NO_REFUND = 30,

  /** 已超过15天无理由退款时限 */
  EXCEED_15_DAYS_LIMIT = 40,

  /** 已发货,请选择退货退款 */
  ALREADY_SHIPPED_CHOOSE_RETURN = 50,

  /** 其他 */
  OTHER = 999,
}

/**
 * 申请货物状态
 * - 1: 未收到货
 * - 2: 已收到货
 */
export enum ApplyGoodsStatus {
  /** 未收到货 */
  NOT_RECEIVED = 1,

  /** 已收到货 */
  RECEIVED = 2,
}

/**
 * 签到icon类型
 * - 1: 默认(金币)
 * - 2: 红包
 * - 3: 礼物
 */
export enum SignInIconType {
  /** 默认(金币) */
  GOLD = 1,

  /** 红包 */
  RED_PACKET = 2,

  /** 礼物 */
  GIFT = 3,
}

/**
 * 签到奖励类型
 * - 1: 每日签到积分
 * - 2: 连签奖励积分
 * - 3: 连签挑战积分
 * - 4: 连签奖励vip
 * - 5: 连签奖励优惠券
 */
export enum SignInRewardType {
  /** 每日签到积分 */
  DAILY_SIGN_IN_POINTS = 1,

  /** 连签奖励积分 */
  CONTINUOUS_SIGN_IN_POINTS = 2,

  /** 连签挑战积分 */
  CONTINUOUS_SIGN_IN_CHALLENGE_POINTS = 3,

  /** 连签奖励vip */
  CONTINUOUS_SIGN_IN_VIP = 4,

  /** 连签奖励优惠券 */
  CONTINUOUS_SIGN_IN_VOUCHER = 5,
}

/**
 * 倒计时状态枚举
 * - NotStarted: 未开始
 * - InProgress: 进行中
 * - Ended: 已结束
 */
export enum CountdownStatusEnum {
  /** 未开始 */
  NotStarted = "not-started",

  /** 进行中 */
  InProgress = "in-progress",

  /** 已结束 */
  Ended = "ended",
}
